import { createSelector } from 'reselect'
import {
  BrandConfig,
  BrandFeaturesConfig,
  EnvConfig,
  Environments,
} from 'src/configurations'
import {
  CoBrands,
  ExtendCurrency,
  ExtendLocales,
  SupportedBrands,
} from 'src/enums'
import { RemoteConfigModel } from 'src/models/remoteConfig.models'
import { supportRequestUrl } from 'src/navigation/urls'
import { RootState } from 'src/redux/createRootReducer'

/** @deprecated - Remove all the selectors using this first, then 🔪 this selector*/
const selectBrandConfigFromState = (state: RootState): BrandConfig =>
  state.session.brandConfig

/**
 * @deprecated
 * * Use the following instead
 * const { BRAND_ID: brandId } = useCurrentBrand()
 */
export const selectBrandId = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.BRAND_ID,
)

/**
 * @deprecated
 * * Use the following instead
 * const { BRAND_NAME: brandName } = useCurrentBrand()
 */
export const selectBrandName = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.BRAND_NAME as SupportedBrands,
)
/**
 * @deprecated
 */
export const selectCoBrandName = createSelector(
  [selectBrandName],
  (brandName) => {
    if (brandName === CoBrands.Pnc.toString()) {
      return CoBrands.Pnc
    } else if (brandName === CoBrands.Bmo.toString()) {
      return CoBrands.Bmo
    }
  },
)

/**
 * @deprecated
 * Use the following instead
 * const { APP_NAME: appName } = useCurrentBrand()
 */
export const selectAppName = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.APP_NAME,
)
/**
 * @deprecated
 * * Use the following instead
 * const { PUSHER_CHANNEL_PREFIX: pusherChannelPrefix } = useCurrentBrand()
 */
export const selectPusherChannelPrefix = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.PUSHER_CHANNEL_PREFIX,
)

/**
 * @deprecated
 * * Use the following instead
 * const { EXTERNAL_TERMS_URL: externalTermsUrl } = useCurrentBrand()
 */
export const selectExternalTermsUrl = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.EXTERNAL_TERMS_URL,
)

/**
 * @deprecated
 * * Use the following instead
 * const { EXTERNAL_FAQ_URL: externalFaqUrl } = useCurrentBrand()
 */
export const selectExternalFAQUrl = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.EXTERNAL_FAQ_URL,
)

/**
 * @deprecated
 * * Use the following instead
 * const { EXTERNAL_PRIVACY_URL: externalPrivacyUrl } = useCurrentBrand()
 */
export const selectExternalPrivacyUrl = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.EXTERNAL_PRIVACY_URL,
)

/**
 * @deprecated
 * * Use the following instead
 * const { EXTERNAL_SUPPORT_URL: externalSupportUrl } = useCurrentBrand()
 */
export const selectExternalSupportUrl = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.EXTERNAL_SUPPORT_URL,
)

export const selectCalculatedSupportLink = createSelector(
  [selectExternalSupportUrl],
  (supportUrl) => supportUrl || supportRequestUrl(),
)

/**
 * @deprecated
 * * Use the following instead
 * const { PRIMARY_DOMAIN: primaryDomain } = useCurrentBrand()
 */
export const selectPrimaryDomain = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.PRIMARY_DOMAIN,
)

/**
 * @deprecated
 * * Use the following instead
 * const { COLOR_FONT_PRIMARY: colorFontPrimary } = useCurrentBrand()
 */
export const selectBrandColorFontPrimary = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.COLOR_FONT_PRIMARY,
)

/**
 * @deprecated
 * * Use the following instead
 * const { COLOR_CTA: colorCta } = useCurrentBrand()
 */
export const selectBrandColorCta = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.COLOR_CTA,
)

/**
 * @deprecated
 * * Use the following instead
 * const { COLOR_HIGHLIGHT: colorHighlight } = useCurrentBrand()
 */
export const selectBrandColorHighlight = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.COLOR_HIGHLIGHT,
)

/**
 * @deprecated
 * * Use the following instead
 * const { APP_STORE_LINK: appStoreLink } = useCurrentBrand()
 */
export const selectBrandAppStoreLink = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.APP_STORE_LINK,
)

/**
 * @deprecated
 * * Use the following instead
 * const { GOOGLE_PLAY_STORE_LINK: googlePlayStoreLink } = useCurrentBrand()
 */
export const selectBrandGooglePlayStoreLink = createSelector(
  [selectBrandConfigFromState],
  (brandConfig) => brandConfig.GOOGLE_PLAY_STORE_LINK,
)

export const selectDefaultCurrencyFromState = (
  state: RootState,
): ExtendCurrency => state.session.defaultCurrency
export const selectDefaultLocaleFromState = (state: RootState): ExtendLocales =>
  state.session.defaultLocale

// TODO: update these
export const selectEnvConfigFromState = (state: RootState): EnvConfig =>
  state.session.envConfig
export const selectRemoteConfigFromState = (
  state: RootState,
): RemoteConfigModel => {
  return state.session.remoteConfig
}
export const selectAstradaVerifyUrlFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) =>
    envConfig.ASTRADA_3DS_VERIFY_BASE_URL ||
    'https://3ds-stage.paywithextend.com/verify.html',
)

// Server-side (SSR) can override via process.env.API_HOST_INTERNAL so axios
// calls made during SSR can reach extend-api via a Docker-internal hostname
// (e.g., http://extend-api:3000) while the browser still sees the public
// API_HOST (e.g., http://localhost:3000). Read directly from process.env
// instead of envConfig so the override is NEVER serialized to the client
// (envConfig ships in window.__INITIAL_STATE__).
//
// No-op in stage/prod where API_HOST_INTERNAL is unset and both sides hit
// the public edge.
//
// Deliberately NOT createSelector: typeof window is a module-global, not
// a Redux state input, so reselect can't track it. Memoizing would cache
// whichever branch fires first and ignore subsequent window-context changes
// within the same module instance. A plain function re-evaluates per call.
export const selectApiHostFromState = (state: RootState): string => {
  if (typeof window === 'undefined') {
    const internal = process.env.API_HOST_INTERNAL
    if (internal) return internal
  }
  const envConfig = selectEnvConfigFromState(state)
  return envConfig.API_HOST || 'https://api-stage.paywithextend.com'
}

export const selectAgentHostFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) =>
    envConfig.AGENT_HOST || 'https://muppeteer-stage.paywithextend.com/api/v1',
)

export const selectAddressulatorHostFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) =>
    envConfig.ADDRESSULATOR_HOST || 'https://address-stage.extend.dev',
)
export const selectProxulatorHostFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) => envConfig.PROXULATOR_HOST || 'https://prox-stage.extend.dev',
)

export const selectWalletHostFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) => envConfig.WALLET_HOST || 'https://wallet-stage.extend.dev/android',
)

export const selectGooglePayIntegratorIdFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) => envConfig.GOOGLE_PAY_INTEGRATOR_ID ?? '',
)

export const selectEnvironmentFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) => envConfig.ENVIRONMENT || Environments.Dev,
)

const DEFAULT_LOCAL_USER_ID = 'u_3W54BGASjSQ81Ta6Q5Yyhg' // from Flyway seed migration

export const selectLocalAuthBypassFromState = createSelector(
  [selectEnvConfigFromState, selectEnvironmentFromState],
  (envConfig, env) =>
    env !== Environments.Prod &&
    env !== Environments.Stage &&
    envConfig.LOCAL_AUTH_BYPASS === 'true',
)

const getLocalUserIdOverride = (): string | null => {
  try {
    return typeof window !== 'undefined' ? localStorage.getItem('LOCAL_USER_ID_OVERRIDE') : null
  } catch {
    return null
  }
}

export const selectLocalUserIdFromState = createSelector(
  [selectEnvConfigFromState, selectLocalAuthBypassFromState],
  (envConfig, localAuthBypass) =>
    localAuthBypass
      ? getLocalUserIdOverride() ||
        envConfig.LOCAL_USER_ID ||
        DEFAULT_LOCAL_USER_ID
      : undefined,
)

export const selectFeedbackSurveyUrlFromState = createSelector(
  [selectEnvConfigFromState],
  (envConfig) => envConfig.FEEDBACK_SURVEY_URL,
)

export const selectBrandFeaturesFromState = (
  state: RootState,
): BrandFeaturesConfig => state.session.brandFeaturesConfig
